import { useState } from 'react';
import type { AudioMode } from '../api/protocol';
import { loadPeerPref, savePeerPref } from '../prefs';
import type { PeerAudioState } from '../spatial/SpatialMixer';
import type { PeerView, VoiceController } from '../voice/VoiceController';
import { Avatar, PEER_AVATAR_SIZE } from './Avatar';
import { Icon } from './Icon';
import { NetIndicator } from './NetIndicator';
import { Slider } from './Slider';
import { teamColor, teamLabel } from './team';

function statusLine(peer: PeerView, audio: PeerAudioState | undefined, mode: AudioMode, myTeam: string | null): { icon: 'radio' | 'prox' | 'users'; text: string } {
  const dist = audio?.distance;
  const d = dist == null ? '' : ` · ${dist < 10 ? dist.toFixed(1) : Math.round(dist)} bl.`;
  if (mode === 'global') return { icon: 'users', text: 'Canale aperto' };
  if (peer.radio || audio?.radio) return { icon: 'radio', text: `Radio${d}` };
  if (peer.teamId && myTeam && peer.teamId === myTeam) return { icon: 'prox', text: `Compagno${d}` };
  return { icon: 'prox', text: `Vicino${d}` };
}

function PlayerCard({
  peer,
  audio,
  mode,
  controller,
  myTeam,
  onHover,
}: {
  peer: PeerView;
  audio: PeerAudioState | undefined;
  mode: AudioMode;
  controller: VoiceController;
  myTeam: string | null;
  onHover: (id: string | null) => void;
}) {
  const [pref, setPref] = useState(() => loadPeerPref(peer.identity));
  const update = (next: { volume: number; muted: boolean }) => {
    setPref(next);
    savePeerPref(peer.identity, next);
    controller.setPeerVolume(peer.identity, next.volume);
    controller.setPeerMuted(peer.identity, next.muted);
  };
  const st = statusLine(peer, audio, mode, myTeam);
  const color = teamColor(peer.teamId);
  const vol = Math.round(pref.volume * 100);

  return (
    <li
      className={`pcard${peer.speaking ? ' is-speaking' : ''}${pref.muted ? ' is-muted' : ''}${audio?.policyMuted ? ' is-policy-muted' : ''}`}
      data-testid="peer-item"
      data-identity={peer.identity}
      data-speaking={peer.speaking ? 'true' : 'false'}
      onMouseEnter={() => onHover(peer.identity)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(peer.identity)}
      onBlur={() => onHover(null)}
    >
      <button
        type="button"
        className={`pcard-mute${pref.muted ? ' is-off' : ''}`}
        onClick={() => update({ ...pref, muted: !pref.muted })}
        aria-pressed={pref.muted}
        aria-label={pref.muted ? `Riattiva ${peer.name}` : `Silenzia ${peer.name} solo per te`}
        title={pref.muted ? 'Riattiva' : 'Silenzia per me'}
        data-testid="peer-mute"
      >
        <Icon name={pref.muted ? 'volumeOff' : 'volume'} size={20} />
      </button>
      <div className="pcard-info">
        <span className="pcard-status" data-testid="peer-distance" title={st.text.replace('bl.', 'blocchi')}>
          <Icon name={st.icon} size={11} />
          {st.text}
        </span>
        <span className="pcard-nameline">
          <strong className="pcard-name">{peer.name}</strong>
        </span>
        <span className="pcard-tags">
          {peer.teamId ? (
            <span className="chip" style={color ? { color, borderColor: color } : undefined}>
              {teamLabel(peer.teamId)}
            </span>
          ) : null}
          {peer.role === 'SPECTATOR' ? <span className="chip chip-dim">Spettatore</span> : null}
          {audio?.policyMuted ? <span className="chip chip-dim">Silenziato dal gioco</span> : null}
          {peer.speaking ? <span className="sr-only">sta parlando</span> : null}
        </span>
      </div>
      <span className={`pcard-head${peer.teamId && myTeam === peer.teamId ? ' is-mate' : ''}`}>
        <Avatar uuid={peer.identity} name={peer.name} size={PEER_AVATAR_SIZE} />
        <NetIndicator net={peer.net} via={peer.via} testId="peer-net" />
      </span>
      <div className="pcard-volume">
        <Slider
          min={0}
          max={200}
          step={5}
          value={vol}
          onChange={(v) => update({ ...pref, volume: v / 100 })}
          variant="tan"
          label={`Volume di ${peer.name}`}
          testId="peer-volume"
        />
        <output className="pcard-vol">{vol}%</output>
      </div>
    </li>
  );
}

export function PlayerGrid({
  peers,
  audio,
  mode,
  controller,
  myTeam,
  emptyHint,
  onHover,
}: {
  peers: PeerView[];
  audio: Map<string, PeerAudioState>;
  mode: AudioMode;
  controller: VoiceController;
  myTeam: string | null;
  emptyHint: string;
  onHover: (id: string | null) => void;
}) {
  // Stable order (by name): cards must not jump around while players move.
  const sorted = [...peers].sort((a, b) => a.name.localeCompare(b.name, 'it'));
  const speaking = peers.filter((p) => p.speaking).length;
  return (
    <section className="players" id="giocatori" aria-labelledby="players-title">
      <h2 id="players-title" className="sr-only">
        Giocatori che senti
      </h2>
      {sorted.length === 0 ? (
        <div className="glass empty-card" data-testid="peer-list-empty">
          <Icon name="users" size={28} />
          <p>{emptyHint}</p>
        </div>
      ) : null}
      <ul className="pgrid" data-testid="peer-list">
        {sorted.map((p) => (
          <PlayerCard key={p.identity} peer={p} audio={audio.get(p.identity)} mode={mode} controller={controller} myTeam={myTeam} onHover={onHover} />
        ))}
      </ul>
      <p className="grid-status" data-testid="voice-summary">
        <span className={`dot${peers.length ? ' is-on' : ''}`} aria-hidden />
        Connesso a <strong data-testid="peer-count">{peers.length}</strong> {peers.length === 1 ? 'giocatore' : 'giocatori'},{' '}
        <strong>{speaking}</strong> {speaking === 1 ? 'sta parlando' : 'stanno parlando'}
      </p>
    </section>
  );
}
